import { useCallback, useRef, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconAddressBook,
  IconCheck,
  IconDots,
  IconFileText,
  IconLock,
  IconLockOpen,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconTruck,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { DataTable, DataTableColumn } from "@/components/DataTable";
import { FormModal } from "@/components/FormModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Can } from "@/components/Can";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { formatAPIError, qk } from "@/lib/apiHelpers";
import type {
  ApprovalRequest,
  ApprovalStatus,
  Carrier,
  CarrierCreateRequest,
  CarrierUpdateRequest,
  CarriersListResponse,
} from "@/types/api";
import { CarrierForm, CarrierFormApi } from "./CarrierForm";
import { CarrierContacts } from "./CarrierContacts";
import { CarrierFleet } from "./CarrierFleet";
import { ContractsDrawer } from "@/components/ContractsDrawer";

function ApprovalBadge({ status, note }: { status: ApprovalStatus; note?: string }) {
  const badge = (() => {
    switch (status) {
      case "pending":
        return <Badge color="yellow" variant="light" size="sm">На модерации</Badge>;
      case "approved":
        return <Badge color="green" variant="light" size="sm">Одобрен</Badge>;
      case "rejected":
        return <Badge color="red" variant="light" size="sm">Отклонён</Badge>;
    }
  })();

  if (status === "rejected" && note) {
    return (
      <Tooltip label={note} withArrow multiline maw={260}>
        <span style={{ cursor: "help" }}>{badge}</span>
      </Tooltip>
    );
  }
  return badge;
}

export default function CarriersPage() {
  const qc = useQueryClient();
  const { hasPerm } = useAuth();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [debounced] = useDebouncedValue(search, 300);

  const filters = { page, page_size: 50, q: debounced, status: statusFilter ?? "" };
  const { data, isLoading } = useQuery({
    queryKey: qk.carriers(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", String(filters.page));
      params.set("page_size", String(filters.page_size));
      if (filters.q) params.set("q", filters.q);
      if (filters.status) params.set("status", filters.status);
      return (await api.get<CarriersListResponse>(`/api/v1/carriers?${params.toString()}`)).data;
    },
  });

  const [editing, setEditing] = useState<Carrier | null | undefined>(undefined);
  // formApi через useRef — см. длинный коммент в Drivers/index.tsx.
  const formApiRef = useRef<CarrierFormApi | null>(null);
  const setFormApi = useCallback((api: CarrierFormApi) => {
    formApiRef.current = api;
  }, []);
  const [confirmDelete, setConfirmDelete] = useState<Carrier | null>(null);
  const [contactsCarrier, setContactsCarrier] = useState<Carrier | null>(null);
  const [fleetCarrier, setFleetCarrier] = useState<Carrier | null>(null);
  const [contractsCarrier, setContractsCarrier] = useState<Carrier | null>(null);

  // Approval modals
  const [approvalTarget, setApprovalTarget] = useState<{ carrier: Carrier; action: "approve" | "reject" } | null>(null);
  const [approvalNote, setApprovalNote] = useState("");

  // INN duplicate warning
  const [innDupWarning, setInnDupWarning] = useState<{ inn: string; existingName: string } | null>(null);
  const [pendingCreate, setPendingCreate] = useState<CarrierCreateRequest | null>(null);

  const createMut = useMutation({
    mutationFn: async (body: CarrierCreateRequest) =>
      (await api.post<Carrier>("/api/v1/carriers", body)).data,
    onSuccess: () => {
      notifications.show({ message: "Перевозчик создан", color: "green" });
      void qc.invalidateQueries({ queryKey: ["carriers"] });
      setEditing(undefined);
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: CarrierUpdateRequest }) =>
      (await api.patch<Carrier>(`/api/v1/carriers/${id}`, body)).data,
    onSuccess: () => {
      notifications.show({ message: "Перевозчик обновлён", color: "green" });
      void qc.invalidateQueries({ queryKey: ["carriers"] });
      setEditing(undefined);
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/api/v1/carriers/${id}`)).data,
    onSuccess: () => {
      notifications.show({ message: "Перевозчик удалён", color: "green" });
      void qc.invalidateQueries({ queryKey: ["carriers"] });
      setConfirmDelete(null);
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const approveMut = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: ApprovalRequest }) =>
      (await api.post<Carrier>(`/api/v1/carriers/${id}/approve`, body)).data,
    onSuccess: () => {
      notifications.show({ message: "Перевозчик одобрен", color: "green" });
      void qc.invalidateQueries({ queryKey: ["carriers"] });
      setApprovalTarget(null);
      setApprovalNote("");
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const rejectMut = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: ApprovalRequest }) =>
      (await api.post<Carrier>(`/api/v1/carriers/${id}/reject`, body)).data,
    onSuccess: () => {
      notifications.show({ message: "Перевозчик отклонён", color: "orange" });
      void qc.invalidateQueries({ queryKey: ["carriers"] });
      setApprovalTarget(null);
      setApprovalNote("");
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const atiRefreshMut = useMutation({
    mutationFn: async (id: string) =>
      (await api.post<Carrier>(`/api/v1/carriers/${id}/ati-refresh`)).data,
    onSuccess: (carrier) => {
      void qc.invalidateQueries({ queryKey: ["carriers"] });
      // Бэк отвечает 200 даже если фирма не найдена (рейтинг = null,
      // updated_at проставляется). Разруливаем три кейса для UX:
      if (carrier.ati_rating == null) {
        notifications.show({
          title: "Фирма не найдена в АТИ",
          message: `Код АТИ "${carrier.ati_code ?? "—"}" не найден на ati.su. Проверьте, что это именно код АТИ (а не ИНН) — их можно посмотреть в личном кабинете ati.su.`,
          color: "yellow",
          autoClose: 8000,
        });
        return;
      }
      const restricted = carrier.work_restricted ? " (ограничен)" : "";
      notifications.show({
        message: `Рейтинг АТИ обновлён: ${carrier.ati_rating.toFixed(1)}${restricted}`,
        color: carrier.work_restricted ? "red" : "green",
      });
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка АТИ", message: formatAPIError(e), color: "red" }),
  });

  const atiUnrestrictMut = useMutation({
    mutationFn: async (id: string) =>
      (await api.post<Carrier>(`/api/v1/carriers/${id}/ati-unrestrict`)).data,
    onSuccess: () => {
      notifications.show({ message: "Ограничение снято", color: "green" });
      void qc.invalidateQueries({ queryKey: ["carriers"] });
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const doCreate = (body: CarrierCreateRequest) => {
    createMut.mutate(body);
  };

  const submit = async () => {
    const formApi = formApiRef.current;
    if (!formApi) return;
    formApi.validate();
    const v = formApi.values;
    // Двойная защита от обхода validate().
    const missing: string[] = [];
    if (!v.name?.trim()) missing.push("Краткое название");
    if (!v.full_name?.trim()) missing.push("Полное название");
    if (!v.inn?.trim()) missing.push("ИНН");
    if (missing.length > 0) {
      formApi.setErrors({
        name: !v.name?.trim() ? "Обязательно" : undefined,
        full_name: !v.full_name?.trim() ? "Обязательно" : undefined,
        inn: !v.inn?.trim() ? "Обязательно" : undefined,
      });
      notifications.show({
        title: "Заполните обязательные поля",
        message: missing.join(", "),
        color: "red",
      });
      return;
    }
    if (formApi.validate().hasErrors) return;
    if (editing) {
      const body: CarrierUpdateRequest = {
        name: v.name,
        full_name: v.full_name,
        inn: v.inn,
        kpp: v.kpp,
        ogrn: v.ogrn,
        ati_code: v.ati_code,
        legal_address: v.legal_address,
        actual_address: v.actual_address,
        bank_account: v.bank_account,
        bank_bik: v.bank_bik,
        bank_name: v.bank_name,
        payment_terms: v.payment_terms,
        payment_days: v.payment_days !== "" ? Number(v.payment_days) : undefined,
        contract_number: v.contract_number,
        contract_date: v.contract_date || undefined,
        mobile: v.mobile,
        phone: v.phone,
        contact_name: v.contact_name,
        notes: v.notes,
      };
      updateMut.mutate({ id: editing.id, body });
    } else {
      const body: CarrierCreateRequest = {
        name: v.name,
        full_name: v.full_name,
        inn: v.inn,
        kpp: v.kpp || undefined,
        ogrn: v.ogrn || undefined,
        ati_code: v.ati_code || undefined,
        legal_address: v.legal_address || undefined,
        actual_address: v.actual_address || undefined,
        bank_account: v.bank_account || undefined,
        bank_bik: v.bank_bik || undefined,
        bank_name: v.bank_name || undefined,
        payment_terms: v.payment_terms || undefined,
        payment_days: v.payment_days !== "" ? Number(v.payment_days) : undefined,
        contract_number: v.contract_number || undefined,
        contract_date: v.contract_date || undefined,
        mobile: v.mobile || undefined,
        phone: v.phone || undefined,
        contact_name: v.contact_name || undefined,
        notes: v.notes || undefined,
      };
      if (v.inn) {
        try {
          const res = await api.get<CarriersListResponse>(`/api/v1/carriers?q=${encodeURIComponent(v.inn)}&page=1&page_size=20`);
          const dup = (res.data.items ?? []).find((c) => c.inn === v.inn);
          if (dup) {
            setPendingCreate(body);
            setInnDupWarning({ inn: v.inn, existingName: dup.name });
            return;
          }
        } catch {
          // On error — proceed without check
        }
      }
      doCreate(body);
    }
  };

  const handleApprovalConfirm = () => {
    if (!approvalTarget) return;
    const body: ApprovalRequest = { note: approvalNote || undefined };
    if (approvalTarget.action === "approve") {
      approveMut.mutate({ id: approvalTarget.carrier.id, body });
    } else {
      rejectMut.mutate({ id: approvalTarget.carrier.id, body });
    }
  };

  const columns: DataTableColumn<Carrier>[] = [
    {
      key: "name",
      header: "Название",
      render: (c) => c.name,
      filter: { type: "text", getValue: (c) => c.name, placeholder: "Поиск по названию" },
    },
    {
      key: "inn",
      header: "ИНН",
      render: (c) => c.inn,
      width: 130,
      filter: { type: "text", getValue: (c) => c.inn, placeholder: "Поиск по ИНН" },
    },
    {
      key: "status",
      header: "Статус",
      width: 130,
      render: (c) => <ApprovalBadge status={c.approval_status} note={c.approval_note} />,
    },
    {
      key: "ati",
      header: "Код АТИ",
      render: (c) =>
        c.ati_code ? (
          <Badge color="teal" variant="light" size="sm">{c.ati_code}</Badge>
        ) : (
          <span style={{ color: "var(--mantine-color-gray-5)" }}>—</span>
        ),
      width: 130,
    },
    {
      key: "ati_rating",
      header: "Рейтинг АТИ",
      width: 140,
      render: (c) => {
        if (c.work_restricted) {
          return (
            <Tooltip label={c.work_restricted_reason ?? "Ограничен"} withArrow multiline maw={260}>
              <Badge color="red" variant="light" size="sm" leftSection={<IconLock size={11} />}>
                Ограничен
              </Badge>
            </Tooltip>
          );
        }
        if (c.ati_rating == null) {
          return <span style={{ color: "var(--mantine-color-gray-5)" }}>—</span>;
        }
        const color = c.ati_rating >= 4.0 ? "green" : c.ati_rating >= 3.0 ? "yellow" : "red";
        return (
          <Badge color={color} variant="light" size="sm">
            {c.ati_rating.toFixed(1)}
          </Badge>
        );
      },
    },
    {
      key: "mobile",
      header: "Телефон",
      render: (c) => c.mobile ?? c.phone ?? "—",
      width: 150,
    },
    {
      key: "contract",
      header: "Договор",
      render: (c) => c.contract_number ?? "—",
      width: 140,
    },
    {
      key: "actions",
      header: "",
      width: 60,
      render: (c) => (
        <Menu shadow="md" position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconAddressBook size={14} />}
              onClick={() => setContactsCarrier(c)}
            >
              Контакты
            </Menu.Item>
            <Menu.Item
              leftSection={<IconTruck size={14} />}
              onClick={() => setFleetCarrier(c)}
            >
              Машины и водители
            </Menu.Item>
            <Menu.Item
              leftSection={<IconFileText size={14} />}
              onClick={() => setContractsCarrier(c)}
            >
              Договоры
            </Menu.Item>
            {hasPerm("carriers:approve") && c.approval_status !== "approved" && (
              <Menu.Item
                leftSection={<IconCheck size={14} />}
                color="green"
                onClick={() => { setApprovalTarget({ carrier: c, action: "approve" }); setApprovalNote(""); }}
              >
                Одобрить
              </Menu.Item>
            )}
            {hasPerm("carriers:approve") && c.approval_status !== "rejected" && (
              <Menu.Item
                leftSection={<IconX size={14} />}
                color="red"
                onClick={() => { setApprovalTarget({ carrier: c, action: "reject" }); setApprovalNote(""); }}
              >
                Отклонить
              </Menu.Item>
            )}
            <Can perm="carriers:write">
              {c.ati_code && (
                <Menu.Item
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => atiRefreshMut.mutate(c.id)}
                >
                  Обновить рейтинг АТИ
                </Menu.Item>
              )}
              <Menu.Item
                leftSection={<IconPencil size={14} />}
                onClick={() => setEditing(c)}
              >
                Редактировать
              </Menu.Item>
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => setConfirmDelete(c)}
              >
                Удалить
              </Menu.Item>
            </Can>
            {hasPerm("carriers:approve") && c.work_restricted && (
              <Menu.Item
                leftSection={<IconLockOpen size={14} />}
                color="orange"
                onClick={() => atiUnrestrictMut.mutate(c.id)}
              >
                Снять ограничение
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      ),
    },
  ];

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Перевозчики</Title>
        <Can perm="carriers:write">
          <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing(null)}>
            Создать
          </Button>
        </Can>
      </Group>

      <Group gap="sm">
        <TextInput
          placeholder="Поиск по названию, ИНН, коду АТИ..."
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
            setPage(1);
          }}
          style={{ maxWidth: 400 }}
        />
        <Select
          placeholder="Все статусы"
          clearable
          data={[
            { value: "pending", label: "На модерации" },
            { value: "approved", label: "Одобрен" },
            { value: "rejected", label: "Отклонён" },
          ]}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
          style={{ width: 180 }}
        />
      </Group>

      <DataTable
        columns={columns}
        rows={data?.items}
        loading={isLoading}
        total={data?.total ?? 0}
        page={page}
        pageSize={50}
        onPageChange={setPage}
        rowKey={(c) => c.id}
        onRowClick={(c) => setEditing(c)}
      />

      <FormModal
        opened={editing !== undefined}
        onClose={() => setEditing(undefined)}
        title={editing ? "Редактировать перевозчика" : "Новый перевозчик"}
        onSubmit={submit}
        loading={createMut.isPending || updateMut.isPending}
        size="xl"
      >
        <CarrierForm initial={editing ?? undefined} onChange={setFormApi} />
      </FormModal>

      <ConfirmDialog
        opened={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteMut.mutate(confirmDelete.id)}
        title="Удалить перевозчика?"
        message={`Перевозчик "${confirmDelete?.name}" будет удалён. Действие необратимо.`}
        loading={deleteMut.isPending}
      />

      {contactsCarrier && (
        <CarrierContacts
          carrier={contactsCarrier}
          opened={!!contactsCarrier}
          onClose={() => setContactsCarrier(null)}
        />
      )}

      {fleetCarrier && (
        <CarrierFleet
          carrier={fleetCarrier}
          opened={!!fleetCarrier}
          onClose={() => setFleetCarrier(null)}
        />
      )}

      {contractsCarrier && (
        <ContractsDrawer
          entityType="carriers"
          entityId={contractsCarrier.id}
          entityName={contractsCarrier.name}
          defaultRole="carrier"
          opened={!!contractsCarrier}
          onClose={() => setContractsCarrier(null)}
        />
      )}

      {/* Approval modal */}
      <Modal
        opened={!!approvalTarget}
        onClose={() => { setApprovalTarget(null); setApprovalNote(""); }}
        title={approvalTarget?.action === "approve" ? "Одобрить перевозчика" : "Отклонить перевозчика"}
        centered
      >
        <Stack gap="md">
          <Text>
            {approvalTarget?.action === "approve"
              ? `Одобрить перевозчика "${approvalTarget?.carrier.name}"?`
              : `Отклонить перевозчика "${approvalTarget?.carrier.name}"?`}
          </Text>
          <Textarea
            label="Комментарий (необязательно)"
            placeholder="Причина или пояснение..."
            value={approvalNote}
            onChange={(e) => setApprovalNote(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { setApprovalTarget(null); setApprovalNote(""); }}>
              Отмена
            </Button>
            <Button
              color={approvalTarget?.action === "approve" ? "green" : "red"}
              onClick={handleApprovalConfirm}
              loading={approveMut.isPending || rejectMut.isPending}
            >
              {approvalTarget?.action === "approve" ? "Одобрить" : "Отклонить"}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={!!innDupWarning}
        onClose={() => { setInnDupWarning(null); setPendingCreate(null); }}
        title="Дублирующийся ИНН"
        centered
      >
        <Stack gap="md">
          <Text>
            Перевозчик с ИНН <strong>{innDupWarning?.inn}</strong> уже существует в системе:{" "}
            <strong>{innDupWarning?.existingName}</strong>. Возможно это филиал. Создать ещё одну запись?
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { setInnDupWarning(null); setPendingCreate(null); }}>
              Отмена
            </Button>
            <Button
              color="orange"
              onClick={() => {
                if (pendingCreate) doCreate(pendingCreate);
                setInnDupWarning(null);
                setPendingCreate(null);
              }}
            >
              Да, создать
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
